import http from './api'
import config from './config'
import { User } from '../types/user'

export interface SignInData {
  email: string
  password: string
}

export interface SignUpData {
  name: string
  email: string
  password: string
}

export interface AuthResponse {
  user: User
  token: string
}

class AuthService {
  async signIn(data: SignInData): Promise<AuthResponse> {
    const response = await http.post('/auth/sign-in', data)
    return response.data
  }

  async signUp(data: SignUpData): Promise<AuthResponse> {
    const response = await http.post('/auth/sign-up', data)
    return response.data
  }

  async forgotPassword(email: string): Promise<{ message: string; resetLink?: string }> {
    const frontendUrl = typeof window !== 'undefined' ? window.location.origin : ''
    const response = await http.post<{ message: string; resetLink?: string }>(
      '/auth/forgot-password',
      { email, ...(frontendUrl && { frontendUrl }) },
      { timeout: 90000 }
    )
    return response.data
  }

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    const response = await http.post<{ message: string }>('/auth/reset-password', {
      token,
      password,
    })
    return response.data
  }

  async signInWithGoogleIdToken(idToken: string): Promise<AuthResponse> {
    const response = await http.post('/auth/google', { idToken })
    return response.data
  }

  /**
   * Fallback: nest-brc Passport Google (authorization code). GIS ID-token
   * is preferred so Google shows academ.one / localhost, not the API host.
   */
  startGoogleSignIn(): void {
    const redirectUri = `${window.location.origin}/auth/callback`
    window.location.href = `${config.baseURL}/api/auth/google?redirect_uri=${encodeURIComponent(redirectUri)}`
  }

  userId(user?: User | null): string {
    if (!user) return ''
    return String(user._id || user.id || '')
  }

  avatarUrl(avatar?: string | null): string | null {
    const path = (avatar || '').trim()
    if (!path) return null
    if (/^https?:\/\//i.test(path)) return path
    const origin = (config.baseURL || '').replace(/\/+$/, '')
    return path.startsWith('/') ? `${origin}${path}` : `${origin}/${path}`
  }

  normalizeUser(raw: any): User {
    const id = String(raw?._id || raw?.id || '')
    return {
      ...(raw || {}),
      _id: id,
      id,
      name: raw?.name || '',
      email: raw?.email || '',
      avatar: raw?.avatar ?? null,
    } as User
  }

  async getCurrentUser(): Promise<User> {
    const response = await http.get('/auth/current-user')
    return this.normalizeUser(response.data)
  }

  async updateProfile(userId: string, data: { name: string; avatar?: File | null }): Promise<User> {
    const form = new FormData()
    form.append('name', data.name)
    if (data.avatar) form.append('avatar', data.avatar)
    const response = await http.patch(`/users/${userId}`, form)
    return this.normalizeUser(response.data)
  }

  async deleteAccount(userId: string): Promise<void> {
    await http.delete(`/users/${userId}`)
  }

  logout(): void {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('authData')
  }

  /**
   * Decode the JWT exp claim without any library.
   * Returns expiry as ms timestamp, or null if unreadable.
   */
  private getTokenExpiry(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return typeof payload.exp === 'number' ? payload.exp * 1000 : null
    } catch {
      return null
    }
  }

  isTokenValid(): boolean {
    const token = this.getToken()
    if (!token) return false

    const expiry = this.getTokenExpiry(token)
    // If we can't read the expiry, fall back to presence check (server will reject if invalid)
    if (expiry === null) return true

    if (Date.now() >= expiry) {
      this.logout()
      return false
    }

    return true
  }

  restoreAuth(): { user: User | null; token: string | null } {
    if (!this.isTokenValid()) {
      return { user: null, token: null }
    }

    const user = this.getUser()
    const token = this.getToken()

    return { user, token }
  }

  getToken(): string | null {
    return localStorage.getItem('token')
  }

  getUser(): User | null {
    const userStr = localStorage.getItem('user')
    if (!userStr) return null
    try {
      return this.normalizeUser(JSON.parse(userStr))
    } catch {
      return null
    }
  }

  setAuthData(user: User, token: string): User {
    const normalized = this.normalizeUser(user)
    const authData = {
      user: normalized,
      token,
      timestamp: Date.now()
    }
    localStorage.setItem('authData', JSON.stringify(authData))
    localStorage.setItem('user', JSON.stringify(normalized))
    localStorage.setItem('token', token)
    return normalized
  }
}

const authService = new AuthService()
export default authService
